import { buildCriterionExpression, Sort as HeadlessSort } from "@coveo/headless";
import { useEffect, useRef, useState } from "react";
import ImageArrow from "components/utility/images/ImageArrow";
import { SortByDictionary } from "../types/coveo-headless-types";

type SortProps = {
	controller: HeadlessSort;
	criteria: SortByDictionary[];
};

const Sort = ({ controller, criteria }: SortProps) => {
	const sortByRef = useRef<HTMLDivElement | null>(null);
	const [state, setState] = useState(controller.state);
	const [selectToggle, setSelectToggle] = useState(false);

	useEffect(() => controller.subscribe(() => setState(controller.state)), [controller]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (sortByRef.current && !sortByRef.current.contains(e.target as Node)) {
				setSelectToggle(false);
			}
		};
		window.addEventListener("scroll", () => {
			setSelectToggle(false);
		});
		window.addEventListener("click", handleClickOutside);
		return () => {
			window.removeEventListener("scroll", () => {
				setSelectToggle(false);
			});
			window.addEventListener("click", handleClickOutside);
		};
	}, []);

	const getCurrentCriterionName = () => {
		const criterion = criteria.find(
			(c) => state.sortCriteria === buildCriterionExpression(c.criterion)
		);
		return criterion ? criterion.name : "";
	};

	return (
		<>
			<div
				className={`coveo-headless-sort t-label-xs-heavy tc-cool-gray-02 ${
					selectToggle ? "open" : ""
				}`}
				onClick={() => setSelectToggle(!selectToggle)}
				onKeyUp={() => setSelectToggle(!selectToggle)}
				tabIndex={0}
				ref={sortByRef}
				role="button"
				aria-label="sort by dropdown menu"
				aria-expanded={selectToggle}
			>
				<span
					className="coveo-headless-sort-value"
					data-qa={`coveo-headless-sort-value-${getCurrentCriterionName()}`}
				>{`sort by: ${getCurrentCriterionName()}`}</span>
				<ImageArrow />
			</div>
			<ul className={`coveo-headless-sort-list ${selectToggle ? "open" : ""}`}>
				{criteria.map((sortInfo) => (
					<li
						aria-label={`sort by ${sortInfo.name}`}
						className={"coveo-headless-sort-item"}
						data-qa={`coveo-headless-sort-item-${sortInfo.name}`}
						key={sortInfo.name}
						onClick={() => {
							setSelectToggle(false);
							controller.sortBy(sortInfo.criterion);
						}}
						onKeyUp={() => {
							setSelectToggle(false);
							controller.sortBy(sortInfo.criterion);
						}}
						role="presentation"
						value={sortInfo.name}
					>
						{sortInfo.name}
					</li>
				))}
			</ul>
		</>
	);
};

export default Sort;
